<template>
  <div class="owc-player" :class="{ 'owc-player--without-modal': !isModal }">
    <div v-show="false" v-html="plyrSvg"></div>
    <video class="owc-player__internal" v-if="internal" ref="player">
      <source :src="source" type="video/mp4" />
      <track v-if="subtitlesSource" :src="subtitlesSource" default />
    </video>
    <cmm-privacy-shield
      ref="shield"
      v-else
      :src="'https://www.youtube.com/embed/' + source"
      :poster="posterImage"
      height="100%"
    ></cmm-privacy-shield>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Plyr, { PlyrEvent, StandardEvent } from 'plyr';
import { PlayerComponent } from './VideoPlayer';
import { VideoType } from './VideoPlayer.enums';
import { PLYR_CONTROLS, PLYR_EVENTS } from './VideoPlayer.config';
import PlayerIcons from '!!raw-loader!plyr/dist/plyr.svg';

/**
 * `VideoPlayer Player` - contains the player itself, the controls and captions for the video using Plyr library
 */
export default (Vue as PlayerComponent).extend({
  name: 'owc-player',
  props: {
    playerData: {
      type: Object,
      required: true,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
    hasUserInteracted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      player: null,
    };
  },
  watch: {
    hasUserInteracted(isUserInteracted) {
      if (isUserInteracted) {
        this.initializePlyr();
      }
    },
  },
  computed: {
    source() {
      return this.playerData?.source;
    },
    internal() {
      return this.playerData?.videoDetails?.type === VideoType.internal;
    },
    subtitlesSource() {
      return this.playerData?.subtitlesSource;
    },
    plyrSvg: function() {
      return PlayerIcons;
    },
    posterImage: function() {
      // forced meanwhile, since cmm-privacy-shield can only handle one image
      return this.playerData.thumbnail?.source?.replace('{mq}', 'mq2');
    },
  },
  mounted() {
    if (!this.internal && this.$refs.shield) {
      (this.$refs.shield as Element).addEventListener('privacyShieldVideoPlay', (ev: any) => {
        this.$emit('trackEvent', { type: 'privacy-shield-video-play', detail: { videoTitle: ev.detail } });
      });
      (this.$refs.shield as Element).addEventListener('privacyShieldVideoPercentagePlayed', (ev: any) => {
        this.$emit('trackEvent', { type: 'privacy-shield-video-percentage-played', detail: ev.detail });
      });
    }
  },
  methods: {
    initializePlyr() {
      // Unfortunately watching the `source` and initialize the playser as soon
      // as the source is set won't work. This will cause the other implementation
      // to malfunction.
      this.$nextTick(() => {
        this.player = new Plyr(this.$refs.player, {
          controls: [
            PLYR_CONTROLS.play,
            PLYR_CONTROLS.progress,
            PLYR_CONTROLS.volume,
            PLYR_CONTROLS.fullscreen,
            PLYR_CONTROLS.captions,
            PLYR_CONTROLS.playLarge,
          ],
          ...this.playerData?.videoPlayerSettings,
        });

        this.player.on(PLYR_EVENTS.ready as StandardEvent, (event: PlyrEvent) => {
          this.$emit('onReady', event.detail.plyr);
        });
      });
    },
  },
});
</script>

<style lang="scss">
@import '~plyr';
</style>
<style lang="scss">
.owc-player {
  // DESIGNER GUIDELINES FOR MVP
  --plyr-color-main: var(--wb-white);
  --plyr-badge-background: var(--wb-blue-45);
  --plyr-badge-text-color: var(--wb-black);
  --plyr-control-spacing: 16px;
  --plyr-control-icon-size: 16px;
  --plyr-control-padding: calc(var(--plyr-control-spacing) * 1.5);
  --plyr-control-radius: 24px;
  --plyr-video-controls-background: linear-gradient(#{rgba($wb-black, 0)}, #{rgba($wb-black, 1)});
  --plyr-video-control-background-hover: #{rgba($wb-black, 0.75)};
  --plyr-video-control-color: var(--wb-white);
  --plyr-control-toggle-checked-background: var(--wb-blue-45);
  --plyr-menu-background: #{rgba($wb-black, 0.75)};
  --plyr-menu-color: var(--wb-white);
  --plyr-menu-radius: 4px;
  --plyr-menu-shadow: 0 1px 2px #{rgba($wb-black, 0.15)};
  --plyr-menu-item-arrow-color: var(--wb-white);
  --plyr-menu-item-arrow-size: 8px;
  --plyr-menu-border-color: var(--wb-black);
  --plyr-menu-border-shadow-color: var(--wb-black);
  --plyr-progress-loading-background: #{rgba($wb-white, 0.1)};
  --plyr-video-progress-buffered-background: #{rgba($wb-white, 0.3)};
  --plyr-audio-progress-buffered-background: #{rgba($wb-white, 0.3)};
  --plyr-range-thumb-height: 16px;
  --plyr-range-fill-background: var(--wb-blue-45);
  --plyr-tooltip-radius: 4px;
  --plyr-font-size-small: 14px;
  --plyr-font-size-base: 16px;
  --plyr-font-family: 'MBCorpo Text', 'DaimlerCS-Regular', sans-serif;

  &--without-modal {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;
  }
  // sometimes the thumbnail and the video can have different ratios
  // we are forcing the player to fit the container in order to match the
  // thumbnail ratio
  .plyr {
    width: 100%;
    height: 100%;
  }
  &__internal {
    object-fit: cover;
  }
  .plyr--youtube {
    // poster prevents dragging the video (360 video, for example) while playing
    .plyr__poster {
      display: none;
    }
  }
}
</style>
